import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Select, Spin, Tag, Typography } from 'antd';
import debounce from 'lodash/debounce';
import lib from '../../assets/utils/lib';

const { Option } = Select;
const { Text } = Typography;

/**
 * Admin picker for the MAIN facility of a branch.
 *
 * Deliberately not SearchableFacilitySelect — that one is enrollee-facing and
 * filters by plan/tier eligibility. Branching is an administrative relationship,
 * so this hits /v2/admin/facilities directly and, by default, only offers
 * facilities that can legally be a main (branch_status=main), because the
 * hierarchy is one level deep.
 *
 * @param {number|string} [excludeFacilityId] omit a facility from the results —
 *   used so a facility is never offered as its own parent.
 */
export default function MainFacilitySelect({
  value,
  onChange,
  placeholder = 'Search facilities…',
  disabled = false,
  allowClear = true,
  excludeFacilityId = null,
  mainsOnly = true,
  size = 'large',
  style,
}) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  // Guards against a slow early request overwriting a fast later one.
  const requestRef = useRef(0);

  const fetchFacilities = useCallback(
    async (search) => {
      const requestId = requestRef.current + 1;
      requestRef.current = requestId;
      setLoading(true);
      try {
        const params = { page: 1, limit: 25 };
        if (search) params.search = search;
        if (mainsOnly) params.branch_status = 'main';

        const parsed = lib.parseApiEnvelope(await lib.getFacilitiesV2(params));
        if (requestRef.current !== requestId) return;

        const rows = Array.isArray(parsed.data) ? parsed.data : (parsed.data?.data || []);
        setOptions(
          rows.filter(
            (row) =>
              String(row.id ?? row.facility_id) !== String(excludeFacilityId ?? ''),
          ),
        );
      } catch {
        if (requestRef.current === requestId) setOptions([]);
      } finally {
        if (requestRef.current === requestId) setLoading(false);
      }
    },
    [excludeFacilityId, mainsOnly],
  );

  const debouncedFetch = useMemo(
    () => debounce((search) => fetchFacilities(search), 350),
    [fetchFacilities],
  );

  useEffect(() => {
    fetchFacilities('');
    return () => debouncedFetch.cancel();
  }, [fetchFacilities, debouncedFetch]);

  return (
    <Select
      showSearch
      filterOption={false}
      value={value ?? undefined}
      onChange={onChange}
      onSearch={debouncedFetch}
      placeholder={placeholder}
      disabled={disabled}
      allowClear={allowClear}
      size={size}
      style={{ width: '100%', ...style }}
      notFoundContent={loading ? <Spin size="small" /> : 'No facilities found'}
      loading={loading}
    >
      {options.map((row) => {
        const id = row.id ?? row.facility_id;
        return (
          <Option key={id} value={id} label={row.name ?? row.facility_name}>
            <div>
              <Text strong>{row.name ?? row.facility_name}</Text>
              {row.branch_count > 0 && (
                <Tag color="blue" style={{ marginLeft: 8 }}>
                  {row.branch_count} branch{row.branch_count === 1 ? '' : 'es'}
                </Tag>
              )}
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {[row.city, row.state].filter(Boolean).join(', ') || '—'}
                </Text>
              </div>
            </div>
          </Option>
        );
      })}
    </Select>
  );
}
